import { Columns3 } from 'lucide-react';
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger, Separator } from '@staff-app/ui';
import { useMdmDevicesViewStore } from '../stores/mdm-devices-view-store';

/**
 * Column customizer popover.
 *
 * Pinned (non-hideable) columns: `status`, `deviceName`.
 * Staff copy of the customizer: VPN IP column IS exposed here (staff-only data
 * per spec §9.3). The apps/web copy omits the `vpnIp` entry entirely so the
 * customer side can never surface the field.
 */
const PINNED_COLUMNS: Array<{ id: string; labelKey: string }> = [
  { id: 'status', labelKey: 'Status' },
  { id: 'deviceName', labelKey: 'Device name' },
];

const OPTIONAL_COLUMNS: Array<{ id: string; labelKey: string }> = [
  { id: 'fleet', labelKey: 'Fleet' },
  { id: 'team', labelKey: 'Team' },
  { id: 'path', labelKey: 'Folder' },
  { id: 'os', labelKey: 'OS' },
  { id: 'osVersion', labelKey: 'OS Version' },
  { id: 'lastHeartBeat', labelKey: 'Last seen' },
  { id: 'model', labelKey: 'Model' },
  { id: 'platform', labelKey: 'Platform' },
  { id: 'localAppVersion', labelKey: 'App version' },
  { id: 'assignType', labelKey: 'Assign type' },
  { id: 'assignName', labelKey: 'Assign name' },
  { id: 'assignId', labelKey: 'Assign id' },
  // Staff delta: VPN IP column exposed ONLY here. apps/web's copy intentionally
  // omits it (spec §9.3 — staff-only data, left-joined from mdm-devices by UUID).
  { id: 'vpnIp', labelKey: 'VPN IP' },
  { id: 'serialNo', labelKey: 'Serial' },
  { id: 'UUID', labelKey: 'UUID' },
  { id: 'tags', labelKey: 'Tags' },
  { id: 'tz', labelKey: 'Timezone' },
  { id: 'activationDate', labelKey: 'Paired' },
];

export function ColumnCustomizer() {
  const columnConfig = useMdmDevicesViewStore((s) => s.columnConfig);
  const setColumnVisibility = useMdmDevicesViewStore((s) => s.setColumnVisibility);
  const resetColumns = useMdmDevicesViewStore((s) => s.resetColumns);

  const visibleSet = new Set(columnConfig.visible);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer gap-1.5"
          aria-label={'Columns'}
          data-testid="mdm-devices-column-customizer"
        >
          <Columns3 className="h-4 w-4" />
          {'Columns'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64" data-testid="mdm-devices-column-popover">
        <div className="flex items-center justify-between pb-2">
          <span className="text-sm font-semibold">{'Columns'}</span>
          <Button
            variant="ghost"
            size="xs"
            onClick={resetColumns}
            className="cursor-pointer"
            data-testid="mdm-devices-column-reset"
          >
            {'Reset'}
          </Button>
        </div>
        <Separator className="mb-2" />
        {/* Scrolls when the column list outgrows the popover (esp. on short viewports). */}
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">

          {PINNED_COLUMNS.map((col) => (
            <label key={col.id} className="flex items-center gap-2 text-sm opacity-60">
              <Checkbox checked disabled aria-label={col.labelKey} />
              <span>{col.labelKey}</span>
            </label>
          ))}
          <Separator className="my-2" />
          {OPTIONAL_COLUMNS.map((col) => {
            const checked = visibleSet.has(col.id);
            return (
              <label
                key={col.id}
                className="flex cursor-pointer items-center gap-2 text-sm hover:bg-accent/50 rounded-sm px-1 py-0.5 transition-colors"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(value) => setColumnVisibility(col.id, value === true)}
                  aria-label={col.labelKey}
                />
                <span>{col.labelKey}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
